import { PlusOutlined } from '@ant-design/icons';
import AppButton from '@nxsys/components/AppButton';
import AppInputSearch from '@nxsys/components/AppInputSearch';
import AppsContent from '@nxsys/components/AppsContainer/AppsContent';
import AppTableContainer from '@nxsys/components/AppTableContainer';
import AppTagStatus from '@nxsys/components/AppTagStatus';
import {
  defaultAttachmentOderDesOptions,
  defaultSubcontractorStatusOption,
  toTotalAttachmentPayload,
} from '@nxsys/constants/AppConstants';
import {
  useCreateAttachmentOrderEmployee,
  useUpdateAttachmentOrderEmployee,
} from '@nxsys/hooks/api';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppDispatch, AppState } from '@nxsys/store';
import { employeeActions } from '@nxsys/store/slices/employee/reducer/employeeSlice';
import { GetAttachmentOrderEmployeeListParams } from '@nxsys/store/slices/employee/types';
import {
  CreateAttachmentOrderEmployeeParams,
  UpdateAttachmentOrderEmployeeParams,
} from '@nxsys/types/api';
import { Icons } from '@nxsys/types/icons';
import { IAttachmentOrder } from '@nxsys/types/models/attachmentOrder';
import { formatStatus } from '@nxsys/utils';
import { showMessage } from '@nxsys/utils/message';
import { Tooltip, Typography } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { debounce, find, toNumber } from 'lodash';
import moment from 'moment';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AttachmentOrderFormValues } from './components/AttachmentOrderForm';
import CreateAttachmentOrderModal from './components/CreateAttachmentOrder';
import EditAttachmentOrderModal from './components/EditAttachmentOrder';

type Props = {
  employeeId: string;
  readOnly?: boolean;
};

const AttachmentOrder: React.FC<Props> = ({ employeeId, readOnly }) => {
  const { messages } = useTypedIntl();
  const dispatch = useDispatch<AppDispatch>();
  const { isEmployee } = useSelector((state: AppState) => state.appStore);

  const { attachmentOrderList } = useSelector(
    (state: AppState) => state.employeeStore,
  );
  const { loading, list, pagination, queryParams } = attachmentOrderList;

  const { run: createAttachmentOrderRun } = useCreateAttachmentOrderEmployee();
  const [openCreateModal, setOpenCreateModal] = useState(false);
  const toggleCreateModal = () => setOpenCreateModal(!openCreateModal);

  const { run: updateAttachmentOrderRun } = useUpdateAttachmentOrderEmployee();
  const [openEditModal, setOpenEditModal] = useState(false);
  const [attachmentOrderSelected, setAttachmentOrderSelected] =
    useState<IAttachmentOrder | null>(null);
  const toggleEditModal = (record?: IAttachmentOrder) => {
    setOpenEditModal(!openEditModal);
    setAttachmentOrderSelected(record || null);
  };

  useEffect(() => {
    handleFetchData({
      page: 0,
      size: 20,
    });
  }, []);

  const handleFetchData = (params: GetAttachmentOrderEmployeeListParams) => {
    const newParams = { ...params, subcontractorId: employeeId };
    dispatch(employeeActions.getAttachmentOrderEmployeeList(newParams));
  };

  const refetch = () =>
    handleFetchData({
      ...queryParams,
    });

  const debouncedFetchData = useCallback(
    debounce((search: string) => {
      handleFetchData({
        ...queryParams,
        page: 0,
        size: 20,
        search: search,
      });
    }, 500),
    [queryParams],
  );

  const onChangePagination = (page: number, pageSize: number) => {
    const isPageSizeChanged = pageSize !== pagination.pageSize;
    const shouldResetPage = isPageSizeChanged && pagination.current > 0;
    handleFetchData({
      ...queryParams,
      page: shouldResetPage ? 0 : page - 1,
      size: pageSize,
    });
  };

  const onChangeTable = (sorter?: { field: string; order: 'desc' | 'asc' }) => {
    if (sorter?.order) {
      handleFetchData({
        ...queryParams,
        sortField: sorter.field || '',
        sortType: sorter.order,
      });
    } else {
      handleFetchData({
        ...queryParams,
        sortField: undefined,
        sortType: undefined,
      });
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    debouncedFetchData(value);
  };

  const columns: ColumnsType<IAttachmentOrder> = [
    ...(!readOnly
      ? [
          {
            key: 'action',
            title: messages['common.action'],
            fixed: 'left' as const,
            align: 'left' as const,
            width: 100,
            render: (_: unknown, record: IAttachmentOrder) => (
              <ul className="flex items-center justify-start gap-x-2">
                <li onClick={() => toggleEditModal(record)}>
                  <Tooltip title="Edit">
                    <span>
                      <Icons.IconEdit />
                    </span>
                  </Tooltip>
                </li>
              </ul>
            ),
          },
        ]
      : []),
    {
      key: 'deductionDesc',
      title: messages['common.deductionDescription'],
      dataIndex: 'deductionDesc',
      sorter: true,
      render: (value) =>
        find(defaultAttachmentOderDesOptions, (item) => item.value === value)
          ?.label || '-',
    },
    {
      key: 'status',
      title: messages['common.status'],
      dataIndex: 'isActive',
      sorter: true,
      render: (value) => {
        const status = value
          ? defaultSubcontractorStatusOption[0].label
          : defaultSubcontractorStatusOption[1].label;
        return (
          <AppTagStatus className={`tag-popup ${formatStatus(status)}`}>
            {formatStatus(status)}
          </AppTagStatus>
        );
      },
    },
    {
      key: 'priority',
      title: messages['common.priority'],
      dataIndex: 'priority',
      sorter: true,
    },
    {
      key: 'deductionMethod',
      title: messages['common.deductionMethod'],
      dataIndex: 'deductionDesc',
      render: (value) =>
        find(defaultAttachmentOderDesOptions, (item) => item.value === value)
          ?.type || 'Table',
    },
    {
      key: 'effectiveDate',
      title: messages['common.effectiveDate'],
      dataIndex: 'effectiveDate',
      render: (value) =>
        value ? moment(value).tz('Europe/London').format('DD/MM/YYYY') : '-',
    },
  ];

  const handleCreate = async (values: AttachmentOrderFormValues) => {
    const selectedOption = defaultAttachmentOderDesOptions.find(
      (item) => item.value === values.deductionDesc?.value,
    );
    const params: CreateAttachmentOrderEmployeeParams = {
      effectiveDate: moment(values.effectiveDate).toISOString(),
      endDate: values.endDate ? moment(values.endDate).toISOString() : undefined,
      type: selectedOption?.type || values.type || 'Table',
      deductionThisPeriod: values.deductionThisPeriod,
      totalDeduction: values.totalDeduction,
      code: selectedOption?.code?.value || values.code,
      deductionDesc: values.deductionDesc?.value || null,
      isActive: values.status?.value === 'ACTIVE',
      priority: values.priority ? toNumber(values.priority) : null,
      deductionRate: toNumber(values.deductionRate),
      protectedMin: toNumber(values.protectedMin),
      protectedMinType: values.protectedMinType ?? 'percentage',
      marginFee: toNumber(values.marginFee),
      orderRef: values.orderRef,
      totalAttachment: toTotalAttachmentPayload(values.totalAttachment),
      subcontractorId: employeeId,
    };
    const res = await createAttachmentOrderRun(params);
    if (res.success) {
      showMessage({
        type: 'success',
        content: messages['common.addSuccess'],
      });
      toggleCreateModal();
      refetch();
    }
  };

  const handleUpdate = async (values: AttachmentOrderFormValues) => {
    if (!attachmentOrderSelected) return;

    const selectedOption = defaultAttachmentOderDesOptions.find(
      (item) => item.value === values.deductionDesc?.value,
    );
    const params: UpdateAttachmentOrderEmployeeParams = {
      effectiveDate: moment(values.effectiveDate).toISOString(),
      endDate: values.endDate ? moment(values.endDate).toISOString() : null,
      type: selectedOption?.type || values.type || 'Table',
      deductionThisPeriod: values.deductionThisPeriod,
      totalDeduction: values.totalDeduction,
      code: selectedOption?.code?.value || values.code,
      deductionDesc: values.deductionDesc?.value || null,
      isActive: values.status?.value === 'ACTIVE',
      priority: values.priority ? toNumber(values.priority) : null,
      deductionRate: toNumber(values.deductionRate),
      protectedMin: toNumber(values.protectedMin),
      protectedMinType: values.protectedMinType ?? 'percentage',
      marginFee: toNumber(values.marginFee),
      orderRef: values.orderRef,
      totalAttachment: toTotalAttachmentPayload(values.totalAttachment),
      subcontractorId: employeeId,
    };
    const res = await updateAttachmentOrderRun(
      attachmentOrderSelected.id,
      params,
    );
    if (res.success) {
      showMessage({
        type: 'success',
        content: messages['common.updateSuccess'],
      });
      toggleEditModal();
      refetch();
    }
  };

  return (
    <div>
      <Typography.Title
        level={5}
        className="!mb-8 !text-[20px] !leading-[1.6] !tracking-[-0.02em] !text-[#1A1D1F]"
      >
        {messages['common.attachmentOrder']}
      </Typography.Title>

      <div className="flex justify-between items-center mb-[16px]">
        <AppInputSearch onChange={handleSearchChange} />
        {!readOnly && (
          <AppButton
            name={messages['common.addNew']}
            leftIcon={<PlusOutlined />}
            onClick={toggleCreateModal}
          />
        )}
      </div>
      <AppsContent>
        <AppTableContainer
          data={list ?? []}
          loading={loading}
          page={pagination.current}
          total={pagination.totalDocs}
          pageSize={pagination.pageSize}
          scroll={{ x: 900, y: 700 }}
          titleEmpty={messages['attachmentOrder.empty']}
          columns={columns}
          onChangePagination={onChangePagination}
          onChangeTable={onChangeTable}
          noDataType={
            isEmployee
              ? 'noDataToShow'
              : !!queryParams?.search
                ? 'noDataFound'
                : 'noDataCreated'
          }
          addNewBtnOnClick={toggleCreateModal}
          dataTypeName={messages['common.attachmentOrder']}
        />
      </AppsContent>
      <CreateAttachmentOrderModal
        isOpen={openCreateModal}
        toggle={toggleCreateModal}
        onSubmit={handleCreate}
      />
      {attachmentOrderSelected && (
        <EditAttachmentOrderModal
          isOpen={openEditModal}
          toggle={toggleEditModal}
          onSubmit={handleUpdate}
          data={attachmentOrderSelected}
        />
      )}
    </div>
  );
};

export default AttachmentOrder;
